import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Wallet, CheckCircle, Upload, FileText, ChevronRight, AlertCircle, CircleDollarSign, Loader2, ChevronDown, ChevronUp, RefreshCcw, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { ownerTermijnService } from '../../api/manpro/ownerTermijnService';
import type { TermijnDto } from '../../api/manpro/types';
import FinanceTabSkeleton from '../../components/FinanceTabSkeleton';

interface OwnerFinanceTabProps {
  projectId: string;
  totalBudget: number;
}

const formatPaymentInformationTitle = (title: unknown) => {
  const normalizedTitle = String(title ?? '').trim().toLowerCase();
  const labels: Record<string, string> = {
    atas_nama: 'Atas Nama',
    nomor_rekening: 'Nomor Rekening',
  };

  return labels[normalizedTitle] ?? normalizedTitle.replace(/[_-]+/g, ' ').replace(/\b\w/g, character => character.toUpperCase());
};

export const OwnerFinanceTab: React.FC<OwnerFinanceTabProps> = ({ projectId, totalBudget }) => {
  const queryClient = useQueryClient();
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedTermijn, setSelectedTermijn] = useState<TermijnDto | null>(null);
  const [expandedTermijnId, setExpandedTermijnId] = useState<string | null>(null);

  // Upload state
  const [bankName, setBankName] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [paymentReceipt, setPaymentReceipt] = useState<File | null>(null);
  const [amount, setAmount] = useState<number | ''>('');
  const [notes, setNotes] = useState('');

  const { data: termijns = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['owner', 'manpro', 'termijns', projectId],
    queryFn: () => ownerTermijnService.getTermijns(projectId),
    staleTime: 1000 * 60 * 5,
  });

  const { data: offlineMethods = [], isLoading: isLoadingMethods } = useQuery({
    queryKey: ['offlinePaymentMethods'],
    queryFn: () => ownerTermijnService.getOfflinePaymentMethods(),
    staleTime: 1000 * 60 * 60,
  });

  const toggleAccordion = (id: string, status: string) => {
    if (status !== 'completed') return;
    setExpandedTermijnId(prev => prev === id ? null : id);
  };

  const uploadProofMutation = useMutation({
    mutationFn: (formData: FormData) => ownerTermijnService.submitPaymentProof(projectId, selectedTermijn!.id, formData),
    onSuccess: () => {
      toast.success('Bukti transfer berhasil diunggah! Menunggu verifikasi admin.');
      queryClient.invalidateQueries({ queryKey: ['owner', 'manpro', 'termijns', projectId] });
      closePaymentModal();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Gagal mengunggah bukti transfer.');
    }
  });

  const reviewWorkMutation = useMutation({
    mutationFn: ({ termijnId, action }: { termijnId: string, action: 'approved' | 'rejected' }) =>
      ownerTermijnService.reviewWork(projectId, termijnId, action),
    onSuccess: (_, variables) => {
      toast.success(variables.action === 'approved' ? 'Pencairan dana disetujui!' : 'Permintaan pencairan ditolak/direvisi.');
      queryClient.invalidateQueries({ queryKey: ['owner', 'manpro', 'termijns', projectId] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Gagal mengirim persetujuan pencairan.');
    }
  });

  const openPaymentModal = (termijn: TermijnDto) => {
    setSelectedTermijn(termijn);
    setBankName('');
    setAccountName('');
    setAccountNumber('');
    setPaymentReceipt(null);
    setAmount(termijn.remaining_amount > 0 ? termijn.remaining_amount : termijn.amount);
    setNotes('');
    setIsPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    setIsPaymentModalOpen(false);
    setSelectedTermijn(null);
  };

  const handleSubmitPayment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentReceipt) {
      toast.error('Harap unggah foto bukti transfer.');
      return;
    }

    const formData = new FormData();
    formData.append('amount', String(amount || selectedTermijn?.amount || 0));
    formData.append('bank_name', bankName);
    formData.append('account_name', accountName);
    formData.append('account_number', accountNumber);
    formData.append('proof_file', paymentReceipt);
    if (notes) {
      formData.append('notes', notes);
    }

    uploadProofMutation.mutate(formData);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft': return <span className="bg-slate-100 text-slate-600 px-3 py-1.5 rounded-lg text-xs font-bold border border-slate-200">Draft</span>;
      case 'pending_payment': return <span className="bg-orange-100 text-orange-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-orange-200">Menunggu Transfer Anda</span>;
      case 'partial_payment': return <span className="bg-orange-100 text-orange-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-orange-300">Dicicil (Sebagian Terbayar)</span>;
      case 'pending_verification': return <span className="bg-yellow-100 text-yellow-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-yellow-200">Verifikasi Admin</span>;
      case 'payment_rejected': return <span className="bg-red-100 text-red-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-red-200">Transfer Ditolak (Upload Ulang)</span>;
      case 'escrowed': return <span className="bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-blue-200">Dana Aman di Escrow</span>;
      case 'pending_owner_approval': return <span className="bg-purple-100 text-purple-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-purple-200 animate-pulse">Menunggu Persetujuan Anda (Cair)</span>;
      case 'work_revision': return <span className="bg-red-100 text-red-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-red-200">Revisi Pekerjaan (Ditolak)</span>;
      case 'ready_for_payout': return <span className="bg-indigo-100 text-indigo-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-indigo-200">Proses Pencairan Admin</span>;
      case 'completed': return <span className="bg-green-100 text-green-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-green-200 flex items-center gap-1"><CheckCircle size={14} /> Selesai (Cair)</span>;
      case 'refunded': return <span className="bg-red-100 text-red-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-red-200 flex items-center gap-1"><XCircle size={14} /> Dana Dikembalikan</span>;
      default: return <span className="bg-slate-100 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-bold">{status}</span>;
    }
  };

  if (isLoading) {
    return <FinanceTabSkeleton />;
  }

  if (isError) {
    return (
      <div className="p-8 text-center text-slate-500">
        Terjadi kesalahan saat memuat data keuangan. <br />
        <button onClick={() => refetch()} className="text-blue-600 hover:text-blue-700">
          <RefreshCcw className="w-4 h-4" />
          Silahkan refresh halaman.
        </button>
      </div>
    )
  }

  if (termijns.length === 0) {
    return (
      <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-10 w-full text-center flex flex-col items-center">
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
          <Wallet className="w-8 h-8 text-slate-400" />
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">Belum Ada Tagihan Aktif</h3>
        <p className="text-slate-500 max-w-md">
          Jadwal pembayaran akan muncul di sini setelah Anda menyetujui Perencanaan (Baseline) dari Provider.
        </p>
      </div>
    );
  }

  const currentTotalAmount = termijns.reduce((acc: number, row: TermijnDto) => acc + (Number(row.amount) || 0), 0);

  return (
    <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-6 lg:p-8 w-full">
      <div className="flex flex-col md:flex-row justify-between md:items-center mb-8 gap-4 border-b border-slate-100 pb-6">
        <div>
          <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-1">
            <CircleDollarSign className="text-blue-600" /> Keuangan & Termin Pembayaran
          </h3>
          <p className="text-sm text-slate-500">Bayar tagihan proyek secara aman melalui Rekening Bersama (Escrow).</p>
        </div>
        <div className="md:text-right bg-slate-50 p-4 rounded-xl border border-slate-200 inline-block">
          <div className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">Total Tagihan Keseluruhan</div>
          <div className="text-2xl font-black text-slate-800">Rp {currentTotalAmount.toLocaleString('id-ID')}</div>
        </div>
      </div>

      <div className="space-y-4">
        {termijns.map((t: TermijnDto, i: number) => {
          const isCompleted = t.status === 'completed';
          const isExpanded = expandedTermijnId === t.id;
          const outboundPayment = (t as any).payments?.find((p: any) => p.type === 'outbound');

          return (
            <div
              key={t.id}
              className={`border ${t.status === 'pending_owner_approval' ? 'border-purple-300 shadow-md bg-purple-50/30' : 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-md'} rounded-2xl transition-all relative overflow-hidden ${isCompleted ? 'cursor-pointer hover:border-green-300' : ''}`}
              onClick={() => toggleAccordion(t.id, t.status)}
            >

              {/* Status Line Indicator */}
              <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${t.status === 'completed' ? 'bg-green-500' : t.status === 'escrowed' ? 'bg-blue-500' : t.status === 'pending_owner_approval' ? 'bg-purple-500' : t.status === 'refunded' ? 'bg-slate-400' : 'bg-orange-400'}`}></div>

              {/* Header Row */}
              <div className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-6 relative">
                <div className="flex gap-5 items-center pl-2">
                  <div className="w-14 h-14 rounded-full bg-blue-50 flex flex-col items-center justify-center border border-blue-100 text-blue-700">
                    <span className="text-xs font-bold leading-none">Termin</span>
                    <span className="text-lg font-black leading-none mt-1">{i + 1}</span>
                  </div>
                  <div>
                    <h4 className="font-extrabold text-slate-800 text-lg">{t.title}</h4>
                    <div className="flex flex-col gap-1 mt-1.5">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-black text-slate-700">Rp {Number(t.amount).toLocaleString('id-ID')}</span>
                        <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                        <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">{t.percentage}%</span>
                      </div>
                      {t.status === 'partial_payment' && (
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs font-bold text-green-700 bg-green-50 px-2 py-1 rounded-md border border-green-100">
                            Cicilan terverifikasi: Rp {Number(t.total_paid || 0).toLocaleString('id-ID')}
                          </span>
                          <span className="text-xs font-bold text-orange-700 bg-orange-50 px-2 py-1 rounded-md border border-orange-100">
                            Sisa: Rp {Number(t.remaining_amount || 0).toLocaleString('id-ID')}
                          </span>
                        </div>
                      )}
                      {t.provider_notes && (t.status === 'pending_owner_approval' || t.status === 'work_revision' || t.status === 'ready_for_payout') && (
                        <div className="mt-2 bg-purple-50 border border-purple-100 rounded-xl p-3 max-w-lg shadow-sm">
                          <span className="text-xs font-bold text-purple-800 uppercase tracking-wider block mb-1">Pesan dari Provider:</span>
                          <p className="text-sm text-purple-700/90 whitespace-pre-wrap">{t.provider_notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 md:pl-0 pl-2">
                  <div className="flex flex-col items-start md:items-end gap-2">
                    {getStatusBadge(t.status)}

                      {t.status !== 'refunded' && (t.status === 'pending_payment' || t.status === 'partial_payment' || t.status === 'payment_rejected') && (
                      <button
                        onClick={(e) => { e.stopPropagation(); openPaymentModal(t); }}
                        className="mt-1 bg-blue-600 text-white text-sm font-bold py-2.5 px-6 rounded-xl hover:bg-blue-700 shadow-sm transition-colors"
                      >
                        Bayar / Upload Struk
                      </button>
                    )}

                    {t.status !== 'refunded' && t.status === 'pending_owner_approval' && (
                      <div className="flex gap-2 mt-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); if (confirm('Tolak dan minta revisi pekerjaan?')) reviewWorkMutation.mutate({ termijnId: t.id, action: 'rejected' }); }}
                          className="bg-white text-red-600 border border-red-200 text-sm font-bold py-2 px-4 rounded-lg hover:bg-red-50 transition-colors"
                        >
                          Tolak untuk Revisi
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); if (confirm('Setujui pencairan dana untuk Provider? Uang akan diteruskan oleh Admin.')) reviewWorkMutation.mutate({ termijnId: t.id, action: 'approved' }); }}
                          className="bg-green-600 text-white text-sm font-bold py-2 px-4 rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                        >
                          Setujui Pekerjaan
                        </button>
                      </div>
                    )}
                  </div>
                  {isCompleted && (
                    <div className="text-slate-400 ml-2">
                      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  )}
                </div>
              </div>

              {/* Accordion Body */}
              {isExpanded && outboundPayment && (
                <div className="border-t border-slate-100 bg-slate-50/50 p-5 pl-7">
                  <h5 className="font-bold text-slate-800 mb-3 text-sm flex items-center gap-2">
                    <CheckCircle size={16} className="text-green-500" /> Detail Bukti Transfer Pencairan (Dari Admin ke Kontraktor)
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-slate-500 font-bold mb-0.5">Bank Tujuan (Provider)</p>
                        <p className="text-sm font-semibold text-slate-900">{outboundPayment.bank_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 font-bold mb-0.5">Nomor Rekening</p>
                        <p className="text-sm font-semibold text-slate-900">{outboundPayment.account_number || '-'}</p>
                        <p className="text-xs text-slate-500 mt-0.5">A.N. {outboundPayment.account_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 font-bold mb-0.5">Nominal Transfer</p>
                        <p className="text-sm font-black text-green-700">Rp {Number(outboundPayment.amount || t.amount).toLocaleString('id-ID')}</p>
                      </div>
                      {outboundPayment.notes && (
                        <div>
                          <p className="text-xs text-slate-500 font-bold mb-0.5">Catatan Admin</p>
                          <p className="text-sm text-slate-700 italic bg-white p-2 rounded-lg border border-slate-200">{outboundPayment.notes}</p>
                        </div>
                      )}
                    </div>

                    <div>
                      <p className="text-xs text-slate-500 font-bold mb-1.5">Struk Bukti Transfer</p>
                      {outboundPayment.proof_url || outboundPayment.proof_file_path ? (
                        <a href={(outboundPayment as any).proof_url || outboundPayment.proof_file_path} target="_blank" rel="noopener noreferrer" className="block w-full max-w-[240px] rounded-xl overflow-hidden border border-slate-200 shadow-sm hover:opacity-90 transition-opacity">
                          <img src={(outboundPayment as any).proof_url || outboundPayment.proof_file_path || ''} alt="Bukti Transfer" className="w-full h-auto object-contain bg-white" />
                        </a>
                      ) : (
                        <div className="w-full max-w-[240px] h-32 bg-slate-100 rounded-xl flex items-center justify-center border border-slate-200 border-dashed text-slate-400 text-sm">
                          Tidak ada struk terlampir
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Payment Modal */}
      {isPaymentModalOpen && selectedTermijn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-blue-50/50">
              <h3 className="font-extrabold text-slate-800 text-lg">Pembayaran Tagihan</h3>
              <button onClick={closePaymentModal} className="p-2 hover:bg-blue-100 rounded-full text-slate-500 transition-colors">
                ✕
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              <div className="bg-blue-600 text-white rounded-2xl p-5 mb-6 shadow-lg shadow-blue-500/20">
                <p className="text-blue-100 text-xs font-bold uppercase tracking-wider mb-1">Total Tagihan (Termin {selectedTermijn.percentage}%)</p>
                <div className="text-3xl font-black tracking-tight">Rp {Number(selectedTermijn.amount).toLocaleString('id-ID')}</div>
                <div className="mt-4 pt-4 border-t border-blue-500/50">
                  <p className="text-blue-100 text-xs mb-3">Pilihan Rekening Transfer (Escrow):</p>
                  {isLoadingMethods ? (
                    <div className="flex items-center gap-2 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" /> Memuat daftar rekening...
                    </div>
                  ) : offlineMethods.length > 0 ? (
                    <div className="space-y-3">
                      {offlineMethods.map((method: any, idx: number) => (
                        <div key={idx} className="bg-blue-700/50 p-3 rounded-lg border border-blue-500/30">
                          <p className="font-bold text-sm mb-1">{method.method_name}</p>
                          {method.payment_information?.map((info: any, i: number) => (
                            <div key={i} className="flex justify-between text-sm mt-1">
                              <span className="text-blue-200">{formatPaymentInformationTitle(info.title)}</span>
                              <span className="font-mono font-medium">{info.data}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <div className="font-mono text-xl font-bold">123-456-7890 (BCA)</div>
                      <p className="text-sm font-bold mt-1">a.n PT Indoteknisi Amanah</p>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200 mb-6">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <p className="text-xs text-amber-800 leading-relaxed font-medium">
                  Pastikan Anda mentransfer tepat sesuai nominal. Uang Anda aman dan tidak akan diteruskan ke Provider sebelum Anda menyetujui hasil kerjanya nanti.
                </p>
              </div>

              <form id="payment-form" onSubmit={handleSubmitPayment} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">Nominal Transfer (Rp)</label>
                  <input
                    type="text"
                    required
                    value={amount ? Number(amount).toLocaleString('id-ID') : ''}
                    onChange={e => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      setAmount(rawValue ? Number(rawValue) : '');
                    }}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-bold text-green-700 bg-green-50"
                    placeholder="Masukkan nominal yang ditransfer"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">Sisa tagihan: Rp {Number(selectedTermijn?.remaining_amount > 0 ? selectedTermijn?.remaining_amount : selectedTermijn?.amount).toLocaleString('id-ID')}</p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">Bank Pengirim</label>
                  <input
                    type="text"
                    required
                    value={bankName}
                    onChange={e => setBankName(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Misal: BCA, Mandiri, BNI"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">Nama Pemilik Rekening</label>
                    <input
                      type="text"
                      required
                      value={accountName}
                      onChange={e => setAccountName(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Atas Nama"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">Nomor Rekening</label>
                    <input
                      type="text"
                      required
                      value={accountNumber}
                      onChange={e => setAccountNumber(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="No. Rek"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">Upload Struk Transfer</label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-slate-300 border-dashed rounded-xl hover:bg-slate-50 transition-colors">
                    <div className="space-y-1 text-center">
                      <Upload className="mx-auto h-8 w-8 text-slate-400" />
                      <div className="flex text-sm text-slate-600 justify-center">
                        <label className="relative cursor-pointer bg-white rounded-md font-bold text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                          <span>Pilih file struk</span>
                          <input
                            type="file"
                            className="sr-only"
                            accept="image/*,.pdf"
                            onChange={e => {
                              if (e.target.files && e.target.files[0]) {
                                setPaymentReceipt(e.target.files[0]);
                              }
                            }}
                          />
                        </label>
                      </div>
                      <p className="text-xs text-slate-500">PNG, JPG, PDF (Maks. 5MB)</p>
                      {paymentReceipt && (
                        <p className="text-xs font-bold text-emerald-600 mt-2 bg-emerald-50 py-1 px-2 rounded-md border border-emerald-100">
                          Terpilih: {paymentReceipt.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">Catatan Tambahan (Opsional)</label>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                    rows={2}
                    placeholder="Masukkan catatan jika ada (Misal: Ini cicilan pertama)..."
                  ></textarea>
                </div>
              </form>
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50">
              <button
                form="payment-form"
                type="submit"
                disabled={uploadProofMutation.isPending}
                className="w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-xl hover:bg-blue-700 shadow-sm transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {uploadProofMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                Kirim Bukti Transfer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
